import axios, { AxiosError, type AxiosResponse } from "axios";
import axiosRetry from "axios-retry";
import { keycloak } from "./keycloak";
import { toast } from "sonner";

const controller = new AbortController();

const axiosWithAuth = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_URL,
  withCredentials: true,
  signal: controller.signal,
});

axiosRetry(axiosWithAuth, {
  retries: 2,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => {
    if (axiosRetry.isNetworkError(error)) {
      return true;
    } else if (
      error.response &&
      (error.response.status === 500 || error.response.status === 401)
    ) {
      return true;
    } else {
      return false;
    }
  },
  async onRetry(retryCount, error, requestConfig) {
    if (retryCount === 1 && error.response && error.response.status === 401) {
      const refreshed = await keycloak.updateToken(-1);
      if (refreshed) {
        const newToken = keycloak.token;
        requestConfig.headers = {
          ...requestConfig.headers,
          Authorization: `Bearer ${newToken ?? ""}`,
        };
        axios.defaults.headers.common["Authorization"] =
          `Bearer ${newToken ?? ""}`;
      }
    }

    if (retryCount === 2 && error.response && error.response.status === 401) {
      toast.error("Your session has expired", { richColors: true });
      setTimeout(() => {
        keycloak.logout();
        controller.abort();
      }, 1500);
    }
  },
});

axiosWithAuth.interceptors.response.use(
  async (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 429) {
      toast.error("Too many requests", { richColors: true });
    }
    return Promise.reject(error);
  }
);

function handleAxiosError(
  error: unknown,
  callback: (response?: AxiosResponse) => void
) {
  const err = error;
  if (err instanceof AxiosError) {
    if (err.response) {
      callback(err.response);
    } else {
      callback();
    }
  } else {
    callback();
  }
}

export { axiosWithAuth, handleAxiosError };
